import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export default function Dashboard({ onSelectClient, onNewClient }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', url: '', description: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          *,
          keyword_campaigns (
            id,
            created_at,
            keywords (id)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (e) {
      setError('Could not load clients — check your Supabase connection.');
      console.error(e);
    }
    setLoading(false);
  };

  const createClient = async () => {
    if (!newClient.name.trim()) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert([{
          name: newClient.name.trim(),
          url: newClient.url.trim(),
          description: newClient.description.trim(),
        }])
        .select()
        .single();

      if (error) throw error;
      setShowNewClient(false);
      setNewClient({ name: '', url: '', description: '' });
      await loadClients();
      // Open the new client straight away
      onSelectClient(data);
    } catch (e) {
      setError('Could not create client.');
      console.error(e);
    }
    setSaving(false);
  };

  const deleteClient = async (e, clientId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this client and all their keyword research? This cannot be undone.')) return;
    await supabase.from('clients').delete().eq('id', clientId);
    await loadClients();
  };

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.url || '').toLowerCase().includes(search.toLowerCase())
  );

  const getCampaignCount = (client) => client.keyword_campaigns?.length || 0;
  const getKeywordCount = (client) => client.keyword_campaigns?.reduce((a, c) => a + (c.keywords?.length || 0), 0) || 0;
  const getLastActivity = (client) => {
    if (!client.keyword_campaigns?.length) return null;
    const dates = client.keyword_campaigns.map(c => new Date(c.created_at));
    return new Date(Math.max(...dates));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <div className="page-title">Clients</div>
          <div className="page-sub">{clients.length} client{clients.length !== 1 ? 's' : ''} · Improve My Search Ranking</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewClient(true)}>
          + New client
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* New client form */}
      {showNewClient && (
        <div className="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--blue-border)', borderWidth: 2 }}>
          <div className="card-title">New client</div>
          <div className="form-row form-row-2 form-group">
            <div>
              <label>Business name *</label>
              <input
                type="text"
                placeholder="e.g. Kent Carpet Cleaners"
                value={newClient.name}
                onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))}
                autoFocus
              />
            </div>
            <div>
              <label>Website URL</label>
              <input
                type="url"
                placeholder="https://example.co.uk"
                value={newClient.url}
                onChange={e => setNewClient(p => ({ ...p, url: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Brief description</label>
            <textarea
              rows={2}
              placeholder="What does this client do?"
              value={newClient.description}
              onChange={e => setNewClient(p => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="form-row form-row-2 form-group">
            <div>
              <label>Phone number</label>
              <input type="text" placeholder="e.g. 01737 773 768" value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <label>Email address</label>
              <input type="email" placeholder="e.g. info@business.co.uk" value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setShowNewClient(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createClient} disabled={saving || !newClient.name.trim()}>
              {saving ? <><span className="spinner" /> Saving…</> : 'Create client'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      {clients.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>
      )}

      {/* Client list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
          <span className="spinner" style={{ width: 20, height: 20 }} /> Loading clients…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--gray-500)' }}>
          {clients.length === 0
            ? <div>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--gray-700)', marginBottom: 8 }}>No clients yet</div>
                <div style={{ fontSize: 13 }}>Create your first client to get started.</div>
              </div>
            : 'No clients match your search.'
          }
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map(client => {
            const lastActivity = getLastActivity(client);
            const campaignCount = getCampaignCount(client);
            const keywordCount = getKeywordCount(client);

            return (
              <div
                key={client.id}
                className="card"
                onClick={() => onSelectClient(client)}
                style={{ cursor: 'pointer', transition: 'border-color 0.12s', marginBottom: 0 }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--gray-200)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {/* Avatar */}
                    <div style={{
                      width: 42, height: 42, borderRadius: 10, background: 'var(--blue-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, fontWeight: 600, color: 'var(--blue-mid)', flexShrink: 0
                    }}>
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--gray-900)', marginBottom: 2 }}>
                        {client.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {client.url || 'No URL set'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--gray-900)' }}>{campaignCount}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>campaign{campaignCount !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--gray-900)' }}>{keywordCount}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>keywords</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {lastActivity
                          ? lastActivity.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'No campaigns yet'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>last activity</div>
                    </div>
                    <button
                      className="btn btn-sm"
                      onClick={e => deleteClient(e, client.id)}
                      style={{ color: 'var(--gray-400)', borderColor: 'transparent' }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
